import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  CommandLineIcon,
  ShieldCheckIcon,
  BoltIcon,
  CubeTransparentIcon,
  WrenchScrewdriverIcon,
  PhotoIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { listTokens } from '../api/vexforge';

export default function Home() {
  const [tokenCount, setTokenCount] = useState(null);

  useEffect(() => {
    listTokens(1000)
      .then((tokens) => setTokenCount(tokens.length))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-24">

      {/* ── Hero ──────────────────────────────────────────────── */}
      <section className="relative text-center pt-8 pb-4">
        {/* Subtle radial glow behind logo */}
        <div className="absolute inset-0 flex justify-center -top-20 pointer-events-none">
          <div className="w-[600px] h-[400px] bg-vextaris-blue/6 blur-[120px] rounded-full" />
        </div>

        {/* Forge logo text */}
        <div className="relative mb-10">
          <h1
            className="text-6xl md:text-8xl font-black tracking-tight select-none"
            style={{
              background: 'linear-gradient(180deg, #ffffff 0%, #70b8ff 40%, #00a3ff 70%, #0066cc 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 30px rgba(0,163,255,0.35)) drop-shadow(0 0 60px rgba(0,163,255,0.15))',
            }}
          >
            VexForge
          </h1>
          <p className="text-gray-500 text-sm tracking-[0.3em] uppercase mt-2">
            Developer Toolchain for Vextaris
          </p>
        </div>

        {/* Tagline */}
        <h2 className="text-2xl md:text-4xl font-semibold text-white max-w-3xl mx-auto leading-tight">
          Build on Vextaris.
          <br />
          <span className="text-gray-400 font-normal">
            Tokens, programs, and on-chain logic — no smart contracts required.
          </span>
        </h2>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
          <Link
            to="/programs"
            className="inline-flex items-center justify-center space-x-2 px-8 py-3.5 bg-white text-vextaris-dark rounded-lg font-semibold hover:bg-gray-100 transition-colors text-base"
          >
            <CommandLineIcon className="w-5 h-5" />
            <span>Build a Program</span>
          </Link>
          <Link
            to="/create"
            className="inline-flex items-center justify-center space-x-2 px-8 py-3.5 border border-gray-700 text-white rounded-lg font-semibold hover:border-gray-500 hover:bg-white/5 transition-colors text-base"
          >
            <span>Create an Asset</span>
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ── What you can build ─────────────────────────────── */}
      <section>
        <div className="text-center mb-12">
          <h3 className="text-xs tracking-[0.25em] uppercase text-gray-500 mb-3">Capabilities</h3>
          <p className="text-2xl font-semibold text-white">Everything ships from one toolchain</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-800/50 rounded-2xl overflow-hidden border border-gray-800/80">
          {[
            {
              title: 'Programs (VIPs)',
              desc: 'Declarative programs that compose native operations. No bytecode, no compiler, no VM. Write JSON, deploy, done.',
              href: '/programs',
              icon: CommandLineIcon,
            },
            {
              title: 'Fungible Tokens (VSC-7)',
              desc: 'Launch tokens with metadata, reputation scoring, and optional presale escrow. Sub-second finality.',
              href: '/create/token',
              icon: CubeTransparentIcon,
            },
            {
              title: 'NFT Collections (VSC-21)',
              desc: 'Create collections with royalties, metadata lock, and on-chain provenance.',
              href: '/nfts',
              icon: PhotoIcon,
            },
            {
              title: 'Multi-Tokens (VSC-55)',
              desc: 'Game items, checkpoint events, mixed fungible/non-fungible collections in one collection.',
              href: '/multi-token',
              icon: CubeTransparentIcon,
            },
            {
              title: 'Presales (VSC-LAUNCH)',
              desc: 'Anti-rug escrow with soft/hard caps, LP auto-lock, and refund guarantees built into the protocol.',
              href: '/presales',
              icon: RocketLaunchIcon,
            },
            {
              title: 'Tools',
              desc: 'Intent terminal (natural language transactions), data anchoring, and token management.',
              href: '/tools',
              icon: WrenchScrewdriverIcon,
            },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.title}
                to={item.href}
                className="bg-vextaris-darker p-8 hover:bg-vextaris-dark/80 transition-colors group"
              >
                <Icon className="w-6 h-6 text-gray-500 group-hover:text-vextaris-blue transition-colors mb-4" />
                <h4 className="text-white font-medium text-lg mb-2 group-hover:text-vextaris-blue transition-colors">
                  {item.title}
                </h4>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── Why VexForge ───────────────────────────────────── */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
        {[
          {
            icon: BoltIcon,
            title: 'Sub-penny, sub-second',
            desc: 'Token creation costs fractions of a VXS. Transactions finalize in under 2 seconds. No gas wars, no MEV.',
          },
          {
            icon: ShieldCheckIcon,
            title: 'Safety by design',
            desc: 'VIPs are statically analyzed at registration. Tokens get reputation scores. Presales have built-in escrow. DYOR tools in the protocol.',
          },
          {
            icon: CommandLineIcon,
            title: 'No smart contracts',
            desc: '104+ native operations replace bytecode. VIPs compose them declaratively. Read the program — it IS the source code.',
          },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.title} className="text-center md:text-left">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-800 mb-4">
                <Icon className="w-5 h-5 text-vextaris-blue" />
              </div>
              <h4 className="text-white font-medium text-lg mb-2">{item.title}</h4>
              <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
            </div>
          );
        })}
      </section>

      {/* ── Stats bar (minimal) ─────────────────────────────── */}
      {tokenCount !== null && (
        <section className="flex justify-center">
          <div className="inline-flex items-center space-x-8 px-8 py-4 rounded-full border border-gray-800 bg-vextaris-darker/50">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{tokenCount}</div>
              <div className="text-xs text-gray-500 mt-0.5">Tokens deployed</div>
            </div>
            <div className="w-px h-8 bg-gray-800" />
            <div className="text-center">
              <div className="text-2xl font-bold text-white">104+</div>
              <div className="text-xs text-gray-500 mt-0.5">Native operations</div>
            </div>
            <div className="w-px h-8 bg-gray-800" />
            <div className="text-center">
              <div className="text-2xl font-bold text-white">~$0.0002</div>
              <div className="text-xs text-gray-500 mt-0.5">Per transaction</div>
            </div>
          </div>
        </section>
      )}

      {/* ── Bottom CTA ──────────────────────────────────────── */}
      <section className="text-center pb-8">
        <p className="text-gray-500 text-sm mb-6">
          Read the docs, then start building.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="https://docs.vextaris.io/developers/vip-guide"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center space-x-2 px-6 py-3 border border-gray-700 text-gray-300 rounded-lg font-medium hover:border-gray-500 hover:text-white transition-colors text-sm"
          >
            <span>VIP Developer Guide</span>
            <ArrowRightIcon className="w-4 h-4" />
          </a>
          <a
            href="https://docs.vextaris.io/developers/rpc"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center space-x-2 px-6 py-3 border border-gray-700 text-gray-300 rounded-lg font-medium hover:border-gray-500 hover:text-white transition-colors text-sm"
          >
            <span>RPC Reference</span>
            <ArrowRightIcon className="w-4 h-4" />
          </a>
          <a
            href="https://docs.vextaris.io/developers/cli"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center space-x-2 px-6 py-3 border border-gray-700 text-gray-300 rounded-lg font-medium hover:border-gray-500 hover:text-white transition-colors text-sm"
          >
            <span>CLI Reference</span>
            <ArrowRightIcon className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
